import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { ReassignmentKind } from '@percent/admin-dashboard/api/actions/donations/donations.types'
import { UseAddDonationToReassignmentQueueFormProps } from './useAddDonationToReassignmentQueueForm.types'
import { isStringDefined } from '@percent/admin-dashboard/common/utility/isDefined'

const reasons = {
  [ReassignmentKind.Unpayable]: [
    'Undeliverable - bank transfer fail',
    'Undeliverable - card payment fail',
    'Undeliverable - cheque returned',
    'Undeliverable - compliance fail',
    'Organisation was set incorrectly'
  ],
  other: 'Other'
}

const createOptions = (items?: string[]) => {
  const allReasons = [...(items || []), reasons.other]

  return allReasons.map(reason => ({
    label: reason,
    value: reason
  }))
}

export function useAddDonationToReassignmentQueueForm({ onFormSubmit }: UseAddDonationToReassignmentQueueFormProps) {
  const { t } = useTranslation()

  const formik = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues: {
      reassignedReason: '',
      customReason: '',
      organisationId: ''
    },
    validationSchema: () =>
      Yup.object().shape({
        reassignedReason: Yup.string().oneOf(Object.values(reasons).flat()).required(t('errorMessage.required')),
        organisationId: Yup.string().trim(),
        customReason: Yup.string()
          .nullable()
          .optional()
          .when('reassignedReason', {
            is: 'Other',
            then: Yup.string()
              .required(t('reassignment.customReason.required'))
              .trim()
              .min(1, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
              .max(255, t('errorMessage.orgFieldCharacterLimit', { fieldName: 'Custom reason' }))
          })
      }),
    onSubmit: async ({ reassignedReason, organisationId, customReason }) => {
      const reason = reassignedReason === reasons.other ? customReason : reassignedReason

      await onFormSubmit({
        organisationId: isStringDefined(organisationId) ? organisationId : undefined,
        reassignmentKind: ReassignmentKind.Unpayable,
        reason: isStringDefined(reason) ? reason : undefined
      })
    }
  })

  return {
    formik,
    reasons,
    reasonOptions: createOptions(reasons[ReassignmentKind.Unpayable])
  }
}
